import { useCallback, useRef } from 'react'
import useOnclickOutside from 'react-cool-onclickoutside'
import {
  COLOR,
  Flex,
  SvgIcon,
  TooltipBasic,
  TooltipSize,
} from '@npco/zeller-design-system'
import { InvoiceItem } from 'features/Invoicing/components/Invoices/Invoice/Invoice.types'

import { ReactComponent as ErrorIcon } from 'assets/svg/error.svg'
import { zIndexMap } from 'const/zIndex'
import { translate } from 'utils/translations'

import {
  ActionButton,
  TooltipContentWrapper,
  TooltipTrigger,
  TooltipWrapper,
} from './InvoiceItemWarningIcon.styled'

export const translations = {
  createNew: translate('shared.createNew'),
  createNewAriaLabel: translate(
    'page.invoice.formSections.items.itemsWarningIconTooltipCreateNew'
  ),
  tooltipAriaLabel: translate(
    'page.invoice.formSections.items.itemsWarningIconShowTooltip'
  ),
  tooltipContent: (itemName: string) =>
    translate('page.invoice.formSections.items.itemsWarningIconTooltip', {
      itemName,
    }),
  updateAriaLabel: translate(
    'page.invoice.formSections.items.itemsWarningIconTooltipUpdate'
  ),
  update: translate('shared.update'),
}

interface TooltipProps {
  item: InvoiceItem
  lineIndex: number
  name: string
  onSaveItem: (item: InvoiceItem) => void
  onUpdateItem: (item: InvoiceItem) => void
  setIsTooltipVisible: (status: boolean) => void
}

const Tooltip = ({
  item,
  lineIndex,
  name,
  onSaveItem: handleOnSaveItem,
  onUpdateItem: handleOnUpdateItem,
  setIsTooltipVisible,
}: TooltipProps) => {
  const tooltipRef = useRef<HTMLDivElement | null>(null)

  useOnclickOutside(() => setIsTooltipVisible(false), {
    refs: [tooltipRef],
    ignoreClass: 'tooltip-trigger',
  })

  return (
    <TooltipContentWrapper className="tooltip-trigger">
      {translations.tooltipContent(name)}
      <Flex justifyContent="space-evenly">
        <ActionButton
          aria-label={translations.updateAriaLabel}
          data-testid={`invoicing-invoice-tooltip-update-${lineIndex}`}
          onClick={() => {
            handleOnUpdateItem(item)
            setIsTooltipVisible(false)
          }}
        >
          {translations.update}
        </ActionButton>
        <ActionButton
          aria-label={translations.createNewAriaLabel}
          data-testid={`invoicing-invoice-tooltip-create-new-${lineIndex}`}
          onClick={() => {
            handleOnSaveItem(item)
            setIsTooltipVisible(false)
          }}
        >
          {translations.createNew}
        </ActionButton>
      </Flex>
    </TooltipContentWrapper>
  )
}

export interface InvoiceItemWarningIconProps {
  item: InvoiceItem
  lineIndex: number
  onSaveItem: (item: InvoiceItem) => void
  onUpdateItem: (item: InvoiceItem) => void
}

export const InvoiceItemWarningIcon = ({
  item,
  lineIndex,
  onSaveItem: handleOnSaveItem,
  onUpdateItem: handleOnUpdateItem,
}: InvoiceItemWarningIconProps) => {
  const { name } = item

  const renderTrigger = useCallback(
    ({ setIsTooltipVisible, ref }) => (
      <TooltipTrigger
        className="tooltip-trigger"
        data-testid={`invoicing-invoice-tooltip-${lineIndex}-trigger`}
        onClick={() => setIsTooltipVisible((status: boolean) => !status)}
        onMouseEnter={() => setIsTooltipVisible(true)}
        ref={ref}
      >
        <SvgIcon
          dataTestId={`invoicing-invoice-tooltip-${lineIndex}-trigger-icon`}
          backgroundColor={COLOR.YELLOW_1000}
        >
          <ErrorIcon />
        </SvgIcon>
      </TooltipTrigger>
    ),
    [lineIndex]
  )

  return (
    <TooltipWrapper>
      <TooltipBasic
        appendTo={document.body}
        closeOnOutsideClick={false}
        dataTestId={`invoicing-invoice-tooltip-${lineIndex}`}
        isInteractive
        placement="top"
        renderTrigger={renderTrigger}
        showArrow={false}
        size={TooltipSize.ExtraSmall}
        zIndex={Number(zIndexMap.tooltip)}
      >
        {({ setIsTooltipVisible }) => (
          <Tooltip
            item={item}
            lineIndex={lineIndex}
            name={name}
            onSaveItem={handleOnSaveItem}
            onUpdateItem={handleOnUpdateItem}
            setIsTooltipVisible={setIsTooltipVisible}
          />
        )}
      </TooltipBasic>
    </TooltipWrapper>
  )
}
